import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, ChevronDownIcon, LogOutIcon, SettingsIcon, ShieldCheckIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAccount } from '../../contexts/AccountContext';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { ConsoleKey } from '../../types/identity';
interface ConsoleSwitcherProps {
  current: ConsoleKey;
  align?: 'left' | 'right';
}
export function ConsoleSwitcher({
  align = 'right'
}: ConsoleSwitcherProps) {
  const {
    account
  } = useAccount();
  const {
    identity,
    logout,
    logoutEverywhere
  } = useAuth();
  const {
    workspaces,
    enterWorkspace,
    isInternal
  } = useWorkspace();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const activePath = typeof window === 'undefined' ? '/' : window.location.pathname;
  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  /* The label on the trigger describes where you actually are, resolved the
     same way as the list rather than from a separate hardcoded table. */
  const active = workspaces.find(space => space.path === activePath) ?? workspaces.find(space => activePath.startsWith(space.path) && space.path !== '/') ?? (isInternal && activePath.startsWith('/admin') ? {
    label: 'Campuzon internal',
    ownerLabel: 'Provisioned staff access',
    roleName: ''
  } : workspaces[0] ?? null);
  return <div className="relative" ref={ref} data-id="element-4850" data-mp="2rf7l9ae4zlbf">
      <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-haspopup="menu" className="flex items-center gap-2 rounded-lg border border-line-strong bg-surface px-2 py-1.5 text-left transition-colors duration-150 ease-out hover:border-ink-subtle" data-id="element-4851" data-mp="2re620imyvsim">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-50 text-[12px] font-semibold text-brand-800" data-id="element-4852" data-mp="33i0iue4oprv8">
          {account.initials}
        </span>
        <span className="hidden leading-tight sm:block" data-id="element-4853" data-mp="38f0por9cyyne">
          <span className="block text-[13px] font-medium text-ink" data-id="element-4854" data-mp="2aue4qbxtz4u4">{active?.label}</span>
          <span className="block max-w-[150px] truncate text-[11px] text-ink-subtle" data-id="element-4855" data-mp="2fro2qfzacotk">
            {active?.ownerLabel}
          </span>
        </span>
        <ChevronDownIcon className="h-4 w-4 text-ink-subtle" data-id="element-4856" data-mp="2kpftv7s3w6wp" />
      </button>

      <AnimatePresence data-id="element-4857" data-mp="2pjtt5l9w2y6n">
        {open && <motion.div role="menu" initial={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} className={`absolute z-40 mt-2 w-72 rounded-card border border-line bg-surface shadow-pop ${align === 'right' ? 'right-0' : 'left-0'}`} data-id="element-4858" data-mp="1s2v2i7bmrut5">
            <div className="border-b border-line px-4 py-3" data-id="element-4859" data-mp="1fienologf90l">
              <p className="text-sm font-semibold text-ink" data-id="element-4860" data-mp="1ak4p013itqa8">
                {identity?.displayName ?? account.fullName}
              </p>
              <p className="text-[12px] text-ink-muted" data-id="element-4861" data-mp="16lnsscln207l">
                @{identity?.username ?? account.username}
              </p>
            </div>
            <div className="px-2 py-2" data-id="element-4862" data-mp="wsyjnodk8yff">
              <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4863" data-mp="wu1a5v6z0yut">
                Switch console
              </p>
              {/*
                Resolved from memberships, not from a role name on the account.
                A workspace appears here only because the authorization engine
                says this identity may enter it — and selecting one still
                re-checks, so the list is a convenience, never a grant.
               */}
              {workspaces.map(space => <button key={space.id} type="button" role="menuitem" onClick={() => {
            setOpen(false);
            if (enterWorkspace(space.id).allowed) navigate(space.path);
          }} className="flex w-full items-start justify-between gap-3 rounded-md px-2 py-2 text-left transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-4864" data-mp="yskojzp6fpxy">
                  <span className="min-w-0" data-id="element-4865" data-mp="fvtaq5sem31a">
                    <span className="block text-[13px] font-medium text-ink" data-id="element-4866" data-mp="juaag49hwb8o">{space.label}</span>
                    <span className="block truncate text-[12px] text-ink-muted" data-id="element-4867" data-mp="2a782st8w0ek7">
                      {space.ownerLabel} · {space.roleName}
                    </span>
                  </span>
                  {space.path === activePath && <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" data-id="element-4868" data-mp="2jn3i8sllmt4" />}
                </button>)}

              {/* Internal administration is listed apart, because it is not a
                  workspace and is not held by any membership. */}
              {isInternal && <button type="button" role="menuitem" onClick={() => {
            setOpen(false);
            navigate('/admin');
          }} className="mt-1 flex w-full items-start justify-between gap-3 rounded-md border-t border-line px-2 pb-2 pt-3 text-left transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-4869" data-mp="m9p1jvwxk8zk">
                  <span className="min-w-0" data-id="element-4870" data-mp="2x0yseetwl1mz">
                    <span className="block text-[13px] font-medium text-ink" data-id="element-4871" data-mp="2x3upk0xxfmad">Campuzon internal</span>
                    <span className="block truncate text-[12px] text-ink-muted" data-id="element-4872" data-mp="2s4rpyw0pu89s">
                      Provisioned staff access · not an account role
                    </span>
                  </span>
                </button>}
            </div>
            <div className="border-t border-line px-2 py-2" data-id="element-4873" data-mp="15wnbciqs9w8w">
              <button type="button" role="menuitem" onClick={() => {
            setOpen(false);
            navigate('/settings');
          }} className="flex min-h-touch w-full items-center gap-2 rounded-md px-2 text-left text-[13px] font-medium text-ink transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-4874" data-mp="19zbkkrjet56e">
                <SettingsIcon className="h-4 w-4 shrink-0 text-ink-muted" data-id="element-4875" data-mp="1tnstj4k750md" />
                Account settings
              </button>
            </div>
            <div className="border-t border-line px-4 py-3" data-id="element-4876" data-mp="1xqpgf7grriw4">
              <p className="flex items-start gap-2 text-[12px] leading-5 text-ink-muted" data-id="element-4877" data-mp="22n7jm2wgb96c">
                <ShieldCheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-700" data-id="element-4878" data-mp="3n6jem26sdpqt" />
                One Campuzon account. Each console only shows data your role is authorized to see.
              </p>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2" data-id="element-4879" data-mp="3e4jv938au9kp">
                <button type="button" onClick={() => {
              setOpen(false);
              logout();
              navigate('/welcome', {
                replace: true
              });
            }} className="inline-flex min-h-touch items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-4880" data-mp="3968lesi07z2h">
                  <LogOutIcon className="h-4 w-4" data-id="element-4881" data-mp="3s5mfrbjnbbti" />
                  Sign out
                </button>
                <button type="button" onClick={() => {
              setOpen(false);
              logoutEverywhere();
              navigate('/welcome', {
                replace: true
              });
            }} className="inline-flex min-h-touch items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-4882" data-mp="tfckua4ew8b0">
                  Sign out everywhere
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}